import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { Box, Button, Stack, Typography } from "@mui/material";
import __ from "src/utils/Translations";
import AddNewUrlButton from "src/Pages/Company/Dashboard/pages/Widgets/MainForm/AddNewUrlButton";
import LinkItem from "src/Pages/Company/Dashboard/pages/Widgets/MainForm/LinkItem";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import DashboardQueries from "src/Pages/Company/Dashboard/services/api/queryOptions";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { useContext, useEffect, useRef, useState } from "react";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import { dynamicNotification } from "src/utils/Notifications";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import {
  NewLinkFormModels,
  NewLinkFormSchema,
} from "src/Pages/Company/Dashboard/pages/Widgets/NewLinkForm/NewLinkForm.models";
import NewLinkForm, {
  NewLinkFormRef,
} from "src/Pages/Company/Dashboard/pages/Widgets/NewLinkForm";
import EditLinkForm, {
  EditLinkFormRef,
} from "src/Pages/Company/Dashboard/pages/Widgets/EditLinkForm";
import MainFormSkeleton from "src/Pages/Company/Dashboard/pages/Widgets/MainForm/MainFormSkeleton";
import useUnsavedChangesGuard from "src/Pages/Company/Dashboard/hooks/useUnsavedChangesGuard";
import useLinkModalActions from "./useLinkModalActions";
import { UnformattedWidgetLink } from "./MainFormLinkForm.models";

interface MainFormValues {
  companyRedirects: UnformattedWidgetLink[];
}

const MAX_LINKS = 20;

type ActiveModal = "new" | "edit" | null;

const WidgetsForm = () => {
  const companyId = useCompanyId();
  const queryClient = useQueryClient();
  const { setModal, resetModal } = useContext(GlobalModalContext);

  const mainFormMethods = useForm<MainFormValues>({
    defaultValues: { companyRedirects: [] },
  });

  const newLinkFormMethods = useForm<NewLinkFormModels>({
    resolver: standardSchemaResolver(NewLinkFormSchema),
    defaultValues: { name: "", url: "", icon: null, iconId: null },
  });

  const editLinkFormMethods = useForm<NewLinkFormModels>({
    resolver: standardSchemaResolver(NewLinkFormSchema),
    defaultValues: { name: "", url: "", icon: null, iconId: null },
  });

  const { fields, append, update, remove } = useFieldArray({
    control: mainFormMethods.control,
    name: "companyRedirects",
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isNewFormCropping, setIsNewFormCropping] = useState(false);
  const [isEditFormCropping, setIsEditFormCropping] = useState(false);
  const activeModalRef = useRef<ActiveModal>(null);
  const newLinkFormRef = useRef<NewLinkFormRef>(null);
  const editLinkFormRef = useRef<EditLinkFormRef>(null);

  const { data, isError, isLoading } = useQuery(
    DashboardQueries.getDashboardModuleCompanyRedirectsWidget.byCompanyId(
      true,
      companyId
    )
  );

  const { mutate: saveLinks } = useMutation({
    ...DashboardQueries.updateCompanyRedirects(companyId),
    onSuccess: () => {
      dynamicNotification(__("Pomyślnie zapisano ustawienia."), "success");
      queryClient.invalidateQueries({
        queryKey:
          DashboardQueries.getDashboardModuleCompanyRedirectsWidget.all(),
      });
    },
    onError: () => {
      dynamicNotification(__("Nie udało się zapisać ustawień."), "error");
    },
  });

  useEffect(() => {
    if (data) {
      mainFormMethods.reset({
        companyRedirects: data,
      });
    }
  }, [data, mainFormMethods]);

  useUnsavedChangesGuard({ isDirty: mainFormMethods.formState.isDirty });

  useEffect(() => {
    if (isError) {
      dynamicNotification(__("Nie udało się pobrać danych."), "error");
    }
  }, [isError]);

  const onSubmit = mainFormMethods.handleSubmit((values) => {
    saveLinks(
      values.companyRedirects.map(({ fileId, name, url }) => ({
        fileId,
        name,
        url,
      }))
    );
  });

  const handleCancel = () => {
    mainFormMethods.reset();
  };

  const modalActions = useLinkModalActions({
    formMethods: newLinkFormMethods,
    onSubmit: (value) => append(value),
    resetModal,
    isCropping: isNewFormCropping,
    beforeSubmit: async () => {
      if (newLinkFormRef.current?.isCropping()) {
        await newLinkFormRef.current.submitCrop();
      }
    },
  });

  const editModalActions = useLinkModalActions({
    formMethods: editLinkFormMethods,
    onSubmit: (value) => {
      if (editingIndex !== null) update(editingIndex, value);
      setEditingIndex(null);
    },
    resetModal,
    submitLabel: __("Zapisz") as string,
    isCropping: isEditFormCropping,
    beforeSubmit: async () => {
      if (editLinkFormRef.current?.isCropping()) {
        await editLinkFormRef.current.submitCrop();
      }
    },
  });

  const prevNewDisabledRef = useRef<boolean | undefined>(undefined);
  const prevEditDisabledRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    const currentDisabled = modalActions[1]?.disabled;
    if (
      activeModalRef.current === "new" &&
      prevNewDisabledRef.current !== currentDisabled
    ) {
      prevNewDisabledRef.current = currentDisabled;
      setModal((prev) =>
        prev.open ? { ...prev, actions: modalActions } : prev
      );
    }
  }, [modalActions, setModal]);

  useEffect(() => {
    const currentDisabled = editModalActions[1]?.disabled;
    if (
      activeModalRef.current === "edit" &&
      prevEditDisabledRef.current !== currentDisabled
    ) {
      prevEditDisabledRef.current = currentDisabled;
      setModal((prev) =>
        prev.open ? { ...prev, actions: editModalActions } : prev
      );
    }
  }, [editModalActions, setModal]);

  if ((isLoading && !data) || isError) {
    return <MainFormSkeleton />;
  }

  return (
    <FormProvider {...mainFormMethods}>
      <form onSubmit={onSubmit} data-testid="widgets-form">
        <Box>
          <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
            <Typography variant="subtitle1" data-testid="widgets-form__title">
              {__("Przydatne linki")}
            </Typography>
            <Stack direction="row" spacing="1rem">
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={!mainFormMethods.formState.isDirty}
                data-testid="widgets-form__cancel-button"
              >
                {__("Anuluj")}
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={!mainFormMethods.formState.isDirty}
                data-testid="widgets-form__save-button"
              >
                {__("Zapisz")}
              </Button>
            </Stack>
          </Stack>
          <Stack
            useFlexGap
            spacing="2rem"
            sx={{
              margin: "1rem 0 0 0",
              flexWrap: "wrap",
              flexDirection: "row",
            }}
            data-testid="widgets-form__links-list"
          >
            {fields.map((field, index) => (
              <LinkItem
                key={field.id}
                name={field.name}
                icon={field.icon}
                onRemove={() => {
                  remove(index);
                }}
                onEdit={() => {
                  editLinkFormMethods.reset({
                    name: field.name,
                    url: field.url,
                    icon: field.icon,
                    iconId: field.fileId,
                  });
                  setEditingIndex(index);
                  activeModalRef.current = "edit";
                  setModal({
                    open: true,
                    title: "Edycja linku",
                    disableOutsideClick: true,
                    maxWidth: 500,
                    customElements: (
                      <EditLinkForm
                        ref={editLinkFormRef}
                        formMethods={editLinkFormMethods}
                        onCroppingChange={setIsEditFormCropping}
                      />
                    ),
                    actions: editModalActions,
                  });
                }}
              />
            ))}
            <AddNewUrlButton
              disabled={fields.length >= MAX_LINKS}
              onClick={() => {
                newLinkFormMethods.reset();
                activeModalRef.current = "new";
                setModal({
                  open: true,
                  title: "Nowy link",
                  disableOutsideClick: true,
                  maxWidth: 500,
                  customElements: (
                    <NewLinkForm
                      ref={newLinkFormRef}
                      formMethods={newLinkFormMethods}
                      onCroppingChange={setIsNewFormCropping}
                    />
                  ),
                  actions: modalActions,
                });
              }}
            />
          </Stack>
        </Box>
      </form>
    </FormProvider>
  );
};

export default WidgetsForm;
